import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/AuthProvider/AuthProvider";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import SpinningWheel from "../SpinningWheel/SpinningWheel";
import getImages from "../../constants/images";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSignIn,
  faTimesCircle,
  faPlusCircle,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import DivsNavBar from "../DivsNavBar/DivsNavBar";

const classNames = (...classes) => {
  return classes.filter(Boolean).join(" ");
};

const NavBar = ({
  menus,
  menuItems,
  itemSubmenus,
  loading,
  error,
  photo,
  screenname,
  weather,
}) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const images = getImages();
  const [openMenu, setOpenMenu] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  // Mensaje del modal cuando el backend rechaza el logout (turno en atención)
  const [logoutBloqueadoMsg, setLogoutBloqueadoMsg] = useState(null);
  const menuRef = useRef(null);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [userMenuRef]);

  if (loading) return <SpinningWheel />;
  if (error) return <div>{error}</div>;

  const handleMenuClick = (menuName) => {
    setOpenMenu(openMenu === menuName ? null : menuName);
    setExpanded(openMenu !== menuName);
    setOpenSubMenu(null);
    setUserMenuOpen(false);
  };

  const handleUserMenuClick = () => {
    setUserMenuOpen(!userMenuOpen);
    setOpenMenu(null);
    setExpanded(false);
  };

  const handleMenuItemHover = (menuItem) => {
    setOpenSubMenu(menuItem);
  };

  const selectedMenuItems = openMenu
    ? menuItems.filter((item) => item.id_menu === openMenu.id)
    : [];
  const userMenuId = menus.find((menu) => menu.name === "UserMenu")?.id;

  const handleMenuItemClick = () => {
    setExpanded(false);
    setOpenMenu(null);
    setOpenSubMenu(null);
  };

  const handleSubMenuClick = () => {
    setExpanded(false);
    setOpenMenu(null);
    setOpenSubMenu(null);
  };

  return (
    <nav className="bg-gradient-to-r from-custom-green-ccard-2 to-custom-green-ccard-1 fixed top-0 left-0 right-0 z-50 h-15">
      <div className="max-w-7xl mx-auto sm:px-2 lg:px-2" ref={menuRef}>
        <div className="relative flex items-center justify-between h-full">
          {/* Logo */}
          <Link to="/Home">
            <img
              className="flex-shrink-0 h-14 w-14 ml-4"
              src={images.logo}
              alt="Centrocard"
            />
          </Link>

          {/* Menús */}
          <div className="flex justify-center flex-1">
            <div className="flex space-x-2">
              {menus
                .filter(
                  (menu) => menu.name !== "Home" && menu.name !== "UserMenu",
                )
                .map((menu) => (
                  <div key={menu.id} className="relative group">
                    <button
                      onClick={() => handleMenuClick(menu)}
                      className={classNames(
                        "px-3 py-2 rounded-lg text-sm font-medium",
                        openMenu?.id === menu.id
                          ? "bg-white text-gray-700"
                          : "text-white hover:bg-white hover:text-gray-700",
                      )}
                      aria-current={
                        openMenu?.name === menu.name ? "page" : undefined
                      }
                    >
                      {menu.name}
                    </button>
                  </div>
                ))}
            </div>
          </div>

          {/* UserMenu */}
          <div className="flex items-center z-50 text-white">
            <div className="items-center text-right font-semibold text-sm px-1">
              <DivsNavBar screenname={screenname} weather={weather} />
            </div>

            <div
              className="relative"
              title="Menú del usuario"
              ref={userMenuRef}
            >
              <button
                onClick={handleUserMenuClick}
                className="text-white flex items-center ps-2 py-1 ml-1 rounded-lg text-sm font-medium hover:bg-white hover:text-gray-700"
              >
                <img
                  className="flex-shrink-0 h-9 w-9 rounded-full"
                  src={photo || images.userImageDefault}
                  alt="Centrocard"
                />
                <ChevronDownIcon className="h-6 w-6" />
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 w-48 bg-white border border-custom-green-ccard-1 rounded-lg shadow-lg mt-0.5">
                  <h1 className="text-center text-gray-800 font-semibold text-lg py-2 border-b border-gray-200">
                    {screenname}
                  </h1>
                  <div className="py-1">
                    {menuItems
                      .filter((item) => item.id_menu === userMenuId)
                      .map((menuItem) => (
                        <Link
                          key={menuItem.id}
                          to={menuItem.url}
                          className="flex items-center justify-between w-full px-4 py-2 text-gray-700 hover:bg-gray-200 focus:bg-gray-200 transition duration-200"
                          onClick={handleMenuItemClick}
                        >
                          <span className="flex-1">{menuItem.name}</span>
                          {images[menuItem.id] && (
                            <span className="flex items-center justify-center w-4 h-4 ml-2">
                              {images[menuItem.id]}
                            </span>
                          )}
                        </Link>
                      ))}
                  </div>
                  <div className="border-t border-gray-200"></div>
                  <Link
                    to="/Login"
                    className="flex items-center justify-between w-full px-4 py-2 bg-gray-300 text-gray-700 hover:text-white hover:bg-gray-400 transition duration-200 rounded-b-lg"
                    onClick={async (e) => {
                      // El logout puede ser rechazado por el backend si el
                      // operador tiene un turno en atención: navegamos solo
                      // si el cierre de sesión fue aceptado.
                      e.preventDefault();
                      try {
                        await auth.logout();
                        navigate("/Login");
                      } catch (err) {
                        setUserMenuOpen(false);
                        setLogoutBloqueadoMsg(
                          err?.message ||
                            "No puede cerrar sesión con un turno en atención. Finalice o derive el turno primero.",
                        );
                      }
                    }}
                  >
                    <span className="flex-1">Cerrar Sesión</span>
                    <FontAwesomeIcon icon={faSignIn} className="w-4 h-4 ml-2" />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Div que expande */}
      <div
        className={`fixed z-40 top-14 left-0 right-0 bg-gradient-to-r transition-all duration-300 ease-in-out ${expanded ? "h-[65vh]" : "h-0 overflow-hidden"}`}
        style={{ overflow: "hidden" }}
      >
        {expanded && (
          <div className="grid grid-cols-4 h-full">
            <div className="col-start-2 p-2">
              {selectedMenuItems.map((menuItem) => (
                <div
                  key={menuItem.id}
                  onMouseEnter={() => handleMenuItemHover(menuItem)}
                  className="relative"
                >
                  <Link
                    to={menuItem.url}
                    className={classNames(
                      "flex justify-between font-semibold items-center p-1 px-2 rounded-lg",
                      openSubMenu?.id === menuItem.id
                        ? "bg-white text-gray-700"
                        : "text-white hover:bg-white hover:text-gray-700",
                    )}
                    onClick={handleMenuItemClick}
                  >
                    <span>{menuItem.name}</span>
                    {itemSubmenus.some(
                      (sub) => sub.id_item_menu === menuItem.id,
                    ) && (
                      <FontAwesomeIcon
                        icon={
                          openSubMenu?.id === menuItem.id
                            ? faTimesCircle
                            : faPlusCircle
                        }
                        className={`ml-2 transition duration-200 ${openSubMenu?.id === menuItem.id ? "rotate-180" : ""}`}
                      />
                    )}
                  </Link>
                </div>
              ))}
            </div>
            <div className="col-start-3 p-2">
              {openSubMenu &&
                itemSubmenus
                  .filter((sub) => sub.id_item_menu === openSubMenu.id)
                  .map((subMenu) => (
                    <Link
                      key={subMenu.id}
                      to={subMenu.url}
                      className="block text-white font-semibold hover:bg-white hover:text-gray-700 p-1 px-2 rounded-lg"
                      onClick={handleSubMenuClick}
                    >
                      {subMenu.name}
                    </Link>
                  ))}
            </div>
            <div className="flex col-start-4 justify-end items-start mr-20 mt-5">
              <button
                onClick={() => {
                  setExpanded(false);
                  setOpenMenu(null);
                }}
                className="items-center justify-center rounded-lg opacity-30 hover:opacity-80 transition-200"
                aria-label="Cerrar menú"
                title="Cerrar Menú"
              >
                <FontAwesomeIcon
                  icon={faTimes}
                  className="text-white text-5xl"
                />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal del sistema: cierre de sesión bloqueado por turno en atención */}
      {logoutBloqueadoMsg && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="logout-bloqueado-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) setLogoutBloqueadoMsg(null);
          }}
        >
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
                <FontAwesomeIcon
                  icon={faTimesCircle}
                  className="h-5 w-5 text-amber-600"
                />
              </div>
              <div>
                <h3
                  id="logout-bloqueado-title"
                  className="text-base font-bold text-gray-800"
                >
                  No se puede cerrar sesión
                </h3>
                <p className="mt-1 text-sm text-gray-600">{logoutBloqueadoMsg}</p>
              </div>
            </div>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                autoFocus
                onClick={() => setLogoutBloqueadoMsg(null)}
                className="rounded-md bg-gray-700 px-4 py-2 text-sm font-semibold text-white transition duration-200 hover:bg-gray-800"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default NavBar;
